import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { AKTA_DOMAIN, ROLES } from '../mock';

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [ready, setReady] = useState(false);

  const bootstrap = useCallback(async () => {
    try {
      const me = await api.me();
      setUser(me);
      const [us, cs, ss] = await Promise.all([
        api.listUsers().catch(() => []),
        api.listClients().catch(() => []),
        api.listSessions({ limit: 500 }).catch(() => []),
      ]);
      setUsers(us); setClients(cs); setSessions(ss);
    } catch {
      setUser(null); setUsers([]); setClients([]); setSessions([]);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    // Skip /auth/me during Emergent auth callback so callback handler wins the race
    if (window.location.hash?.includes('session_id=')) {
      setReady(true);
      return;
    }
    bootstrap();
  }, [bootstrap]);

  const refreshData = useCallback(async () => {
    try {
      const [us, cs, ss] = await Promise.all([
        api.listUsers().catch(() => []),
        api.listClients().catch(() => []),
        api.listSessions({ limit: 500 }).catch(() => []),
      ]);
      setUsers(us); setClients(cs); setSessions(ss);
    } catch { /* noop */ }
  }, []);

  const loginDemo = async (email) => {
    const u = await api.demoLogin(email);
    setUser(u);
    await refreshData();
    return u;
  };

  const finishGoogleLogin = async (session_id) => {
    const u = await api.loginWithSessionId(session_id);
    setUser(u);
    await refreshData();
    return u;
  };

  const logout = async () => {
    try { await api.logout(); } catch { /* noop */ }
    setUser(null); setClients([]); setSessions([]); setUsers([]);
  };

  const addClient = async (body) => {
    const c = await api.createClient(body);
    setClients((prev) => [c, ...prev]);
    return c;
  };

  const patchClient = async (id, body) => {
    const c = await api.patchClient(id, body);
    setClients((prev) => prev.map((x) => (x.id === id ? c : x)));
    return c;
  };

  const upsertSessionLocal = (s) => {
    setSessions((prev) => {
      const idx = prev.findIndex((x) => x.id === s.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = s; return next; }
      return [s, ...prev];
    });
  };

  const createSession = async (body) => { const s = await api.createSession(body); upsertSessionLocal(s); return s; };
  const patchSession = async (id, body) => { const s = await api.patchSession(id, body); upsertSessionLocal(s); return s; };
  const addConversation = async (id, text) => { const s = await api.addConversation(id, text); upsertSessionLocal(s); return s; };
  const uploadSignedReport = async (id, file, onProgress) => {
    const sr = await api.uploadSignedReport(id, file, onProgress);
    // refresh single session
    const s = await api.getSession(id); upsertSessionLocal(s);
    return sr;
  };
  const deleteSignedReport = async (id) => {
    await api.deleteSignedReport(id);
    const s = await api.getSession(id); upsertSessionLocal(s);
  };

  // Role-scoped views (backend already filters; below are helpers)
  const visibleRmIds = useMemo(() => {
    if (!user) return [];
    if (user.role === ROLES.ADMIN) return users.filter((u) => u.role === ROLES.RM).map((u) => u.user_id);
    if (user.role === ROLES.SALES_HEAD) return user.rm_ids || [];
    if (user.role === ROLES.RM) return [user.user_id];
    return [];
  }, [user, users]);

  const rmById = useCallback((id) => users.find((u) => (u.user_id === id || u.id === id) && u.role === ROLES.RM), [users]);
  const salesHeadById = useCallback((id) => users.find((u) => (u.user_id === id || u.id === id) && u.role === ROLES.SALES_HEAD), [users]);

  const value = useMemo(() => ({
    ready, user, users, clients, sessions,
    visibleClients: clients, visibleSessions: sessions, visibleRmIds,
    ROLES, USERS: users, AKTA_DOMAIN,
    loginDemo, finishGoogleLogin, logout, refreshData,
    addClient, updateClient: patchClient,
    createSession, patchSession, upsertSession: upsertSessionLocal,
    appendConversation: addConversation,
    setSignedReport: async () => { /* deprecated — use uploadSignedReport */ },
    uploadSignedReport, deleteSignedReport,
    rmById, salesHeadById,
    getClient: (id) => clients.find((c) => c.id === id),
    getSessionsFor: (clientId) => sessions.filter((s) => s.client_id === clientId).sort((a, b) => new Date(b.session_date) - new Date(a.session_date)),
    getSession: (id) => sessions.find((s) => s.id === id),
    canAccessClient: (clientId) => !!clients.find((c) => c.id === clientId),
  }), [ready, user, users, clients, sessions, visibleRmIds, rmById, salesHeadById]); // eslint-disable-line react-hooks/exhaustive-deps

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};
